
import React, { lazy, Suspense } from 'react';
import HeroSection from '@/components/HeroSection';
import TrustElementsHome from '@/components/contact/TrustElementsHome';
import SEOMetadata from '@/components/SEOMetadata';
import AdvancedPerformanceOptimizer from '@/components/seo/AdvancedPerformanceOptimizer';
import LocalSEOStructuredData from '@/components/seo/LocalSEOStructuredData';
import TechnicalSEOEnhancer from '@/components/seo/TechnicalSEOEnhancer';
import { localBusinessData, businessServices } from '@/data/seo/localBusinessData';
import { webPageStructuredData, personStructuredData, websiteStructuredData } from '@/data/structuredData';

// Lazy load Below-the-Fold components for better performance
const CTASection = lazy(() => import('@/components/CTASection'));
const AboutSection = lazy(() => import('@/components/home/AboutSection'));
const TestimonialsSectionHome = lazy(() => import('@/components/home/TestimonialsSectionHome'));
const VideoIntroSection = lazy(() => import('@/components/home/VideoIntroSection'));
const ApproachSection = lazy(() => import('@/components/home/ApproachSection'));
const UnifiedServicesSection = lazy(() => import('@/components/home/UnifiedServicesSection'));
const BlogTeaserSection = lazy(() => import('@/components/home/BlogTeaserSection'));

const IndexPage = () => {
  return (
    <>
      <SEOMetadata
        metaTitle="Klarheit finden, wenn alles laut ist | Executive Coach & Finanzcoach Bayern"
        metaDescription="Coaching für Führungskräfte und Selbstentscheider. Souverän entscheiden in Führung und Vermögen – in Bayern, am Rothsee und remote."
        title="Klarheit finden, wenn alles laut ist | Executive Coach & Finanzcoach Bayern"
        description="Coaching für Führungskräfte und Selbstentscheider. Souverän entscheiden in Führung und Vermögen – in Bayern, am Rothsee und remote."
        imageUrl="/images/og-portrait.jpg"
        isHomepage={true}
        keywords="Executive Coaching Nürnberg, Finanzcoaching Bayern, Katalysator-Führung, Führungskräfte-Coaching München, Outdoor Coaching, Remote Coaching"
        canonicalUrl="https://joerg-hoermann.de"
      />
      
      {/* Phase 3: Advanced SEO Optimizations */}
      <AdvancedPerformanceOptimizer 
        preloadImages={['/lovable-uploads/5461ac67-8c69-475b-9d16-969a9f4d2d3a.webp']}
        prefetchUrls={['/finanzcoaching', '/executive-coaching', '/outdoor-coaching']}
        dnsPreconnect={[]}
        criticalResources={[]}
      />
      
      <LocalSEOStructuredData 
        businessData={localBusinessData}
        services={businessServices as any}
      />
      
      <TechnicalSEOEnhancer 
        canonicalUrl="https://joerg-hoermann.de"
        alternateUrls={[
          { href: "https://joerg-hoermann.de", hreflang: "de-DE" },
          { href: "https://joerg-hoermann.de", hreflang: "x-default" }
        ]}
        structuredData={[
          personStructuredData,
          websiteStructuredData,
          webPageStructuredData({
            name: "Executive Coaching & Finanzcoaching | Jörg Hörmann",
            description: "Executive Coaching für Führungskräfte & Finanzcoaching für Privatpersonen. Katalysator-Führung statt klassische Beratung. Bayern & deutschlandweit.",
            url: "https://joerg-hoermann.de",
            aboutTopics: ["executiveCoaching", "finanzcoaching", "katalysatorFuehrung", "emotionaleIntelligenz"]
          })
        ]}
      />
      <main>
      {/* AI-Summary / AEO Abstract */}
      <section className="sr-only" aria-label="Zusammenfassung" data-speakable="summary">
        <p>Jörg Hörmann ist Executive Coach und Finanzcoach in Bayern. Er begleitet Führungskräfte mit der Katalysator-Führung Methode und Privatpersonen bei finanzieller Selbstbestimmung. Angebote: Executive Coaching, Finanzcoaching, Outdoor-Coaching am Rothsee. Standort: Metropolregion Nürnberg, auch remote.</p>
      </section>
      {/* Hero image preload is in index.html for instant LCP discovery */}
      
      {/* 1. Hero Section - Critical above-the-fold content */}
      <HeroSection
        title="Klarheit finden, wenn alles andere laut ist."
        subtitle="Für Führungskräfte und Selbstentscheider in Bayern"
        description={"Ich begleite Führungskräfte und Privatpersonen bei wichtigen Entscheidungen. Ob finanzielle Selbstbestimmung oder Führungsexzellenz. Individuelle\u00A0Begleitung statt standardisierte Lösungen."}
        imageUrl="/lovable-uploads/5461ac67-8c69-475b-9d16-969a9f4d2d3a.jpg"
        backgroundClass="home-hero-professional" 
        isHomepage={true}
        imageObjectPosition="object-center"
      />
      
      {/* 2. Trust indicators section - Critical for immediate credibility */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto">
          <TrustElementsHome />
        </div>
      </section>
      
      {/* Below-the-fold content with lazy loading and loading fallbacks */}
      <Suspense fallback={<div className="py-16 bg-joerg-50 animate-pulse"><div className="container mx-auto h-32"></div></div>}>
        <AboutSection />
      </Suspense>

      <Suspense fallback={<div className="py-16 bg-white animate-pulse"><div className="container mx-auto h-48"></div></div>}>
        <UnifiedServicesSection />
      </Suspense>
      
      <Suspense fallback={<div className="py-16 bg-joerg-50 animate-pulse"><div className="container mx-auto h-40"></div></div>}>
        <ApproachSection />
      </Suspense>
      
      {/* Golden Sentence – emotionaler Break */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <p className="max-w-2xl mx-auto text-center italic text-joerg-700 text-lg lg:text-xl leading-loose">
            „Manche Entscheidungen brauchen keine weitere Analyse. Sondern einen Raum, in dem Denken und Fühlen wieder zusammenfinden."
          </p>
        </div>
      </section>

      <Suspense fallback={<div className="py-16 bg-white animate-pulse"><div className="container mx-auto h-36"></div></div>}>
        <VideoIntroSection />
      </Suspense>
      
      <Suspense fallback={<div className="py-16 bg-joerg-50 animate-pulse"><div className="container mx-auto h-44"></div></div>}>
        <TestimonialsSectionHome />
      </Suspense>

      <Suspense fallback={<div className="py-16 bg-warm-white animate-pulse"><div className="container mx-auto h-48"></div></div>}>
        <BlogTeaserSection />
      </Suspense>
      
      <Suspense fallback={<div className="py-16 animate-pulse"><div className="container mx-auto h-32"></div></div>}>
        <CTASection
          title="Ein ruhiges Gespräch – unverbindlich und kostenfrei"
          description="Manchmal braucht es nur einen ersten Schritt. Lassen Sie uns in Ruhe herausfinden, ob und wie ich Sie begleiten kann."
          primaryCTA={{
            text: "Gesprächstermin wählen",
            link: "https://zeeg.me/joerghoermann",
            isExternal: true
          }}
          backgroundClass="bg-joerg-50"
        />
        </Suspense>
      </main>
    </>
  );
};

export default IndexPage;
